import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Container, Dropdown, Row } from "react-bootstrap";
import { RxCross1 } from "react-icons/rx";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import searchIcon from "../../../../assests/svg/searchIcon.svg";
import {
  notifyBugsnagError,
  removeDashed,
  shouldTriggerSearch,
  showToast,
} from "../../../../common/utils/Functions";
import DashboardLayout from "../../../../components/layouts/DashboardLayout";
import PageHeading from "../../../../components/pageHeading";
import RangeDateModal from "../../../../components/rangeDateModal/rangeDateModal";
import ReactExcel from "../../../../components/reactExcel/ReactExcel";
import ReactTable from "../../../../components/reactTable/ReactTable";
import InputField from "../../../../components/theme/InputField";
import ReactSelectField from "../../../../components/theme/ReactSelectField";
import useFetch from "../../../../hooks/useFetch";
import { OrganizationItem } from "../../../../types/organization";
import styles from "../reporting.module.scss";
import ReviewText from "../../../../pages/adminDashboard/reporting/reviews/ReviewText";
import ReviewsRatingSummary from "./ReviewsRatingSummary";

const Reviews = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { uuid } = useParams();

  const [reviewsData, setReviewsData] = useState<any>();
  const [searchValue, setSearchValue] = useState("");
  const [sortBy, setSortBy] = useState<1 | 2 | 3 | null>(null);
  const [sortByValue, setSortByValue] = useState<any>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [showRange, setShowRange] = useState(false);
  const [excelData, setExcelData] = useState<any>([]);
  const [filterValues, setFilterValues] = useState<any>([]);
  const [employeeFilterValues, setEmployeeFilterValues] = useState<any>([]);
  const [employeeValues, setEmployeeValues] = useState<any>(null);
  const [serviceFilterValue, setServiceFilterValue] = useState<any>([]);
  const [serviceValue, setServiceValue] = useState<any>(null);
  const [propertyValue, setPropertyValue] = useState<any>(null);
  const organizationUuid = null;
  const [propertyUuid, setPropertyUuid] = useState(null);
  const [employeeUuid, setEmployeeUuid] = useState(null);
  const [serviceUuid, setServiceUuid] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [ratingSummary, setRatingSummary] = useState<{
    averageRating: number;
    totalReviews: number;
    distribution: { stars: number; count: number }[];
  } | null>(null);

  const [pageLimit, setPageLimit] = useState<number>(10);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const getServiceScore = (item: any) => {
    const raw =
      item?.rating ?? item?.star ?? item?.service_score ?? item?.serviceScore;
    if (raw === null || raw === undefined || raw === "") return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  };

  const getReviewText = (item: any) =>
    item?.comment ?? item?.review_text ?? item?.note ?? "";

  const buildRatingSummary = (data: any, list: any[] = []) => {
    const ratingBreakdown = Array.isArray(data?.rating_breakdown)
      ? data.rating_breakdown
      : null;
    const distributionSource =
      data?.rating_distribution ??
      data?.distribution ??
      data?.stars_count ??
      null;

    let distribution = [
      { stars: 5, count: 0 },
      { stars: 4, count: 0 },
      { stars: 3, count: 0 },
      { stars: 2, count: 0 },
      { stars: 1, count: 0 },
    ];

    if (ratingBreakdown) {
      // Prefer API rating_breakdown for the full filtered set (stable across pages)
      const countsByStar: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      ratingBreakdown.forEach((item: any) => {
        const star = Number(item?.star ?? item?.stars);
        if (star >= 1 && star <= 5) {
          countsByStar[star] = Number(item?.count ?? 0);
        }
      });
      distribution = distribution.map(({ stars }) => ({
        stars,
        count: countsByStar[stars] || 0,
      }));
    } else if (distributionSource) {
      distribution = distribution.map(({ stars }) => ({
        stars,
        count: Number(
          distributionSource?.[stars] ??
            distributionSource?.[`${stars}`] ??
            distributionSource?.[`star_${stars}`] ??
            distributionSource?.[`star${stars}`] ??
            data?.[`star_${stars}_count`] ??
            data?.[`rating_${stars}_count`] ??
            0
        ),
      }));
    } else {
      const counts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      list.forEach((item) => {
        const score = getServiceScore(item);
        if (score) {
          const rounded = Math.min(5, Math.max(1, Math.round(score)));
          counts[rounded] += 1;
        }
      });
      distribution = distribution.map(({ stars }) => ({
        stars,
        count: counts[stars] || 0,
      }));
    }

    const totalFromDistribution = distribution.reduce(
      (sum, item) => sum + item.count,
      0
    );
    const totalReviews = Number(
      data?.total_reviews ??
        data?.totalReviews ??
        data?.totalRecords ??
        totalFromDistribution ??
        0
    );

    const averageFromApi =
      data?.average_rating ??
      data?.averageRating ??
      data?.avg_rating ??
      data?.service_score ??
      null;

    const averageRating =
      averageFromApi != null && Number.isFinite(Number(averageFromApi))
        ? Number(averageFromApi)
        : totalFromDistribution
          ? distribution.reduce(
              (sum, item) => sum + item.stars * item.count,
              0
            ) / totalFromDistribution
          : 0;

    return { averageRating, totalReviews, distribution };
  };

  const handleCloseRange = () => setShowRange(false);
  const handleShowRange = () => setShowRange(true);

  const [
    getReviewsData,
    {
      response: reviewsDataResponse,
      error: reviewsDataError,
      loading: reviewsDataLoading,
    },
  ] = useFetch("/dashboard/all-ratings", {
    method: "POST",
  });

  const [
    getExcelData,
    {
      response: excelDataResponse,
      error: excelDataError,
      loading: excelDataLoading,
    },
  ] = useFetch("/dashboard/all-ratings", {
    method: "POST",
  });

  const [propDataApi, { response: propDataResponse, error: propDataError }] =
    useFetch("/property/list", {
      method: "post",
    });

  const [
    employeeDataApi,
    { response: employeeDataResponse, error: employeeDataError },
  ] = useFetch("/propertyemployees/list", {
    method: "post",
  });

  const [
    serviceDataApi,
    { response: serviceDataResponse, error: serviceDataError },
  ] = useFetch("/organization/all-property-services", {
    method: "post",
  });

  useEffect(() => {
    if (reviewsDataResponse) {
      const list = reviewsDataResponse.data?.list || [];
      setTotalRecords(reviewsDataResponse.data?.totalRecords);
      setRatingSummary(buildRatingSummary(reviewsDataResponse.data, list));

      const updatedList = list.map((item: any) => {
        const serviceScore = getServiceScore(item);
        const reviewText = getReviewText(item);

        return {
          propertyName: item?.property_details?.name ?? removeDashed,
          service:
            item?.property_service_details?.service_details?.name ??
            removeDashed,
          employee: item?.property_employee_details?.name ?? removeDashed,
          serviceScore:
            serviceScore != null ? Number(serviceScore).toFixed(1) : removeDashed,
          reviewText: <ReviewText text={reviewText || "--"} />,
          date: moment(item?.createdAt).format("MM/DD/YYYY h:mm A"),
        };
      });
      setReviewsData(updatedList);
    }
    if (reviewsDataError) {
      showToast(reviewsDataError.message as string, "error");
    }
  }, [reviewsDataResponse, reviewsDataError]);

  useEffect(() => {
    if (!shouldTriggerSearch(searchValue)) return;
    const timer = setTimeout(() => {
      try {
        getReviewsData({
          start: (currentPage - 1) * pageLimit,
          limit: pageLimit,
          startDate,
          endDate,
          organization_uuid: organizationUuid,
          property_uuid: propertyUuid,
          employee_uuid: employeeUuid,
          service_uuid: serviceUuid,
          sortedBy: sortBy,
          search: searchValue,
        });
      } catch (e: any) {
        notifyBugsnagError(e, { api: "getReviewsData" });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [
    searchValue,
    currentPage,
    pageLimit,
    sortBy,
    startDate,
    endDate,
    organizationUuid,
    propertyUuid,
    employeeUuid,
    serviceUuid,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        propDataApi({
          start: 0,
          limit: -1,
          search: "",
          is_active: 1,
        });
        employeeDataApi({
          start: 0,
          limit: -1,
          search: "",
          property_uuid: propertyUuid,
          is_active: 1,
        });
        serviceDataApi({
          property_uuid: propertyUuid,
        });
      } catch (e: any) {
        notifyBugsnagError(e, {
          api: "propDataApi || employeeDataApi || serviceDataApi",
        });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [propertyUuid]);

  useEffect(() => {
    if (excelDataResponse) {
      const updateOrgExcel = excelDataResponse.data.list?.map((item: any) => {
        const serviceScore = getServiceScore(item);
        const reviewText = getReviewText(item);

        return {
          prop_name: item?.property_details?.name ?? removeDashed,
          service:
            item?.property_service_details?.service_details?.name ??
            removeDashed,
          employee: item?.property_employee_details?.name ?? removeDashed,
          service_score:
            serviceScore != null
              ? Number(serviceScore).toFixed(1)
              : removeDashed,
          review_text: reviewText || removeDashed,
          date:
            moment(item?.createdAt).format("MM/DD/YYYY h:mm A") ?? removeDashed,
        };
      });
      setExcelData([...updateOrgExcel]);
    }
    if (excelDataError) {
      showToast(excelDataError.message as string, "error");
    }
  }, [excelDataResponse, excelDataError]);

  const handleExcelApi = () => {
    try {
      getExcelData({
        start: 0,
        limit: -1,
        startDate,
        endDate,
        organization_uuid: organizationUuid,
        property_uuid: propertyUuid,
        employee_uuid: employeeUuid,
        service_uuid: serviceUuid,
        sortedBy: sortBy,
        search: searchValue,
      });
    } catch (e: any) {
      notifyBugsnagError(excelDataError, {
        api: "getExcelData",
      });
    }
  };

  useEffect(() => {
    let updateOrg;
    if (propDataResponse) {
      updateOrg = propDataResponse?.data?.list?.map(
        (item: OrganizationItem) => ({
          value: item?.uuid,
          label: item?.name,
        })
      );
    }
    if (employeeDataResponse) {
      const empData = employeeDataResponse?.data?.list?.map(
        (item: OrganizationItem) => ({
          value: item?.uuid,
          label: item?.name,
        })
      );
      setEmployeeFilterValues([{ label: "All", value: null }, ...empData]);
    }
    if (serviceDataResponse) {
      const serviceData = serviceDataResponse?.data?.map(
        (item: OrganizationItem) => ({
          value: item?.service_details?.uuid,
          label: item?.service_details?.name,
        })
      );
      setServiceFilterValue([{ label: "All", value: null }, ...serviceData]);
    }

    if (updateOrg) {
      setFilterValues([{ label: "All", value: null }, ...updateOrg]);
    }
  }, [
    propDataResponse,
    propDataError,
    serviceDataResponse,
    employeeDataResponse,
  ]);

  const handleDateFilter = (value: string) => {
    const today = moment().format("MM/DD/YYYY");
    let newStartDate = null;
    let newEndDate = null;
    switch (value) {
      case "week":
        newStartDate = moment().startOf("week").format("MM/DD/YYYY");
        break;
      case "month":
        newStartDate = moment().startOf("month").format("MM/DD/YYYY");
        break;
      case "today":
        newStartDate = today;
        break;
      case "all":
        break;
      default:
        break;
    }
    if (value !== "all" && newStartDate) {
      newEndDate = today;
    }
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const filters = [
    { value: "all", label: "All" },
    { value: "today", label: "Today" },
    { value: "week", label: "This Week" },
    { value: "month", label: "This Month" },
    { value: "Custom", label: "Custom" },
  ];

  const options = [
    { value: 1, label: "Date" },
    { value: 2, label: "Highest Review" },
    { value: 3, label: "Lowest Review" },
  ];

  const hasProperties = Boolean(propDataResponse?.data?.list?.length);

  const headings = hasProperties
    ? [
        { label: "Property Name", key: "propertyName" },
        { label: "Service", key: "service" },
        { label: "Employee", key: "employee" },
        { label: "Service Score", key: "serviceScore" },
        { label: "Review Text", key: "reviewText" },
        { label: "Review Date/Time", key: "date" },
      ]
    : [
        { label: "Service Score", key: "serviceScore" },
        { label: "Review Text", key: "reviewText" },
        { label: "Review Date/Time", key: "date" },
      ];

  const excelHeaders = [
    { key: "prop_name", header: "Property Name", width: 40 },
    { key: "service", header: "Service", width: 40 },
    { key: "employee", header: "Employee", width: 40 },
    { key: "service_score", header: "Service Score", width: 20 },
    { key: "review_text", header: "Review Text", width: 50 },
    { key: "date", header: "Review Date/Time", width: 40 },
  ];

  useEffect(() => {
    if (location.state?.hasOwnProperty("filterValue")) {
      setSelectedFilter(location.state.filterValue);
      handleDateFilter(location.state.filterValue.value);
    }
  }, [location]);

  const getReportingPath = (path = "") =>
    uuid ? `/${uuid}/reporting${path}` : `/reporting${path}`;

  const OrgDropdownItems = () => (
    <div
      style={{
        border: "1px solid #A9B0BF",
        paddingLeft: "11px",
        width: "34px",
        borderRadius: "4px",
        backgroundColor: "#ededed",
      }}
    >
      <Dropdown align="end" className={styles.dropDots}>
        <Dropdown.Toggle variant="light" id="reviews-dropdown">
          ...
        </Dropdown.Toggle>
        <Dropdown.Menu
          popperConfig={{
            modifiers: [
              {
                name: "offset",
                options: { offset: [24, 8] },
              },
            ],
          }}
          style={{
            maxWidth: "max-content",
            minWidth: "150px",
            zIndex: "99",
          }}
        >
          <Dropdown.Item onClick={() => navigate(getReportingPath())}>
            Tips
          </Dropdown.Item>
          <Dropdown.Item onClick={() => navigate(getReportingPath("/reviews"))}>
            Reviews
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );

  return (
    <DashboardLayout>
      <div className={styles.dash_page}>
        <Container>
          <Row className="g-xl-4 g-3">
            <Col xxl={12} xl={12}>
              <PageHeading
                heading={
                  <span style={{ display: "flex", gap: "10px" }}>
                    Reviews
                    <OrgDropdownItems />
                  </span>
                }
                subHeading="Here is the information about all reviews."
              >
                {hasProperties && (
                  <div className={styles.search}>
                    <div className={styles.searchField}>
                      <InputField
                        name="search_property_name"
                        icon={searchIcon}
                        placeholder="Search by Property Name"
                        value={searchValue}
                        onChange={(e) => {
                          setCurrentPage(1);
                          setSearchValue(e.target.value);
                        }}
                        autoComplete="off"
                      />
                      {searchValue ? (
                        <span
                          className={styles.crossIcon}
                          onClick={() => setSearchValue("")}
                        >
                          <RxCross1 />
                        </span>
                      ) : null}
                    </div>
                  </div>
                )}
              </PageHeading>
            </Col>
            <div className={styles.tipsWrap}>
              <ReactSelectField
                name="sortBy"
                options={options}
                label="Sort By"
                value={sortByValue}
                onChange={(e: any) => {
                  setCurrentPage(1);
                  setSortBy(e.value);
                  setSortByValue(e);
                }}
              />
              <ReactSelectField
                name="filterBy"
                options={filters}
                label="Filter By"
                value={selectedFilter}
                onChange={(e: any) => {
                  if (e.value === "Custom") {
                    handleShowRange();
                  }
                  handleDateFilter(e.value);
                  setSelectedFilter(e);
                }}
              />
              {hasProperties && (
                <ReactSelectField
                  name="properties"
                  options={filterValues}
                  label="Properties"
                  value={propertyValue}
                  onChange={(e: any) => {
                    setPropertyUuid(e.value);
                    setPropertyValue(e);
                    setEmployeeValues(null);
                    setEmployeeUuid(null);
                    setServiceUuid(null);
                    setServiceValue(null);
                  }}
                />
              )}
              {hasProperties && (
                <ReactSelectField
                  value={serviceValue}
                  name="services"
                  options={serviceFilterValue}
                  label="Services"
                  onChange={(e: any) => {
                    setServiceUuid(e.value);
                    setServiceValue(e);
                  }}
                />
              )}
              {hasProperties && (
                <ReactSelectField
                  name="employee"
                  options={employeeFilterValues}
                  label="Employees"
                  onChange={(e: any) => {
                    setEmployeeValues(e);
                    setEmployeeUuid(e.value);
                  }}
                  value={employeeValues}
                />
              )}
              <ReactExcel
                setExcelData={setExcelData}
                loading={excelDataLoading}
                onClick={handleExcelApi}
                buttonTitle="Export CSV"
                fileType="xlsx"
                fileName="Reviews List"
                coulmns={excelHeaders}
                excelData={excelData}
              />
            </div>
            <ReviewsRatingSummary data={ratingSummary} />

            <ReactTable
              data={reviewsData || []}
              headings={headings}
              currentPage={currentPage}
              itemPerPage={pageLimit}
              totalItems={totalRecords}
              setPerPageLimit={setPageLimit}
              pageLimit={pageLimit}
              setCurrentPage={setCurrentPage}
              pagination={true}
              statusColumn={false}
              loading={reviewsDataLoading || !reviewsData}
              actionColumn={false}
            />
          </Row>
          {selectedFilter?.value === "Custom" && (
            <RangeDateModal
              show={showRange}
              handleClose={handleCloseRange}
              setEndDate={setEndDate}
              setStartDate={setStartDate}
            />
          )}
        </Container>
      </div>
    </DashboardLayout>
  );
};

export default Reviews;
